/*
 * Ada Chatbot integration adapter
 *
 * Minimal wrapper that injects Ada embed script and exposes open/close helpers.
 */

export type AdaHandle = string

type AdaEmbedPayload = Record<string, unknown>
type AdaEmbedFunction = ((cmd: string, payload?: AdaEmbedPayload) => void) & { q?: unknown[] }

declare global {
  interface Window {
    adaEmbed?: AdaEmbedFunction
    adaSettings?: Record<string, unknown>
  }
}

export interface AdaConfig {
  handle: AdaHandle
  language?: string
  meta?: Record<string, unknown>
  settings?: Record<string, unknown>
}

let adaLoaded = false
let adaLoading: Promise<void> | null = null

export function loadAda(config: AdaConfig): Promise<void> {
  if (adaLoaded) return Promise.resolve()
  if (adaLoading) return adaLoading

  adaLoading = new Promise<void>((resolve: () => void) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve()
      return
    }

    const w = window
    const d = document

    // Initialize queue stub
    if (!w.adaEmbed) {
      const queue: unknown[] = []
      const stub = (...args: unknown[]) => { queue.push(args) }
      stub.q = queue
      w.adaEmbed = stub as AdaEmbedFunction
    }
    w.adaSettings = {
      handle: config.handle,
      language: config.language || 'en',
      meta: config.meta || {},
      ...(config.settings || {})
    }

    const script = d.createElement('script')
    script.async = true
    script.src = 'https://static.ada.support/embed2.js'
    script.onload = () => { adaLoaded = true; resolve() }
    script.onerror = () => { resolve() } // resolve to avoid blocking
    const first = d.getElementsByTagName('script')[0]
    first?.parentNode?.insertBefore(script, first)
  })

  return adaLoading
}

export function openAda(message?: string) {
  if (typeof window === 'undefined') return
  if (message && window.adaEmbed) {
    try { window.adaEmbed('setMeta', { initialMessage: message }) } catch { /* ignore */ }
  }
  try { if (window.adaEmbed) window.adaEmbed('show') } catch { /* ignore */ }
}

export function closeAda() {
  if (typeof window === 'undefined') return
  try { if (window.adaEmbed) window.adaEmbed('hide') } catch { /* ignore */ }
}

